import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { environment } from '../../environments/environment';
import { JobService } from '../services/job.service';
import { PageHeaderComponent } from '../shared/components/page-header/page-header.component';
import { LoadingSpinnerComponent } from '../shared/components/loading-spinner/loading-spinner.component';

interface SpotRow {
  id: number;
  name: string | null;
  location: string | null;
  visit_count: number;
  total_minutes: number;
  last_visited: string | null;
  catch_count: number;
  conditions_confidence: number | null;
}

@Component({
  selector: 'app-spots',
  templateUrl: './spots.component.html',
  styleUrls: ['./spots.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule, DatePipe, FormsModule, MatProgressSpinnerModule, MatTableModule, PageHeaderComponent, LoadingSpinnerComponent],
})
export class SpotsComponent implements OnInit, OnDestroy {
  spots = new MatTableDataSource<SpotRow>();
  displayedColumns = ['name', 'visits', 'time', 'catches', 'last_visited', 'conditions'];
  isLoading = true;

  isRecomputing = false;
  recomputeStatus = '';

  showAddForm = false;
  newLat = '';
  newLng = '';
  newName = '';
  newLocation = '';
  isSavingNew = false;

  private jobSub: Subscription | null = null;

  constructor(private http: HttpClient, private jobService: JobService) {}

  ngOnInit(): void {
    this.loadSpots();
    this.jobSub = this.jobService.completion$.subscribe(job => {
      if (job.entity_type === 'spot' || job.job_type === 'spots_recompute') {
        this.loadSpots();
      }
    });
  }

  ngOnDestroy(): void {
    this.jobSub?.unsubscribe();
  }

  loadSpots(): void {
    this.isLoading = true;
    this.http.get<SpotRow[]>(`${environment.apiUrl}/fishing_spots`).subscribe({
      next: (data) => {
        this.spots.data = data.sort((a, b) => b.total_minutes - a.total_minutes);
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; }
    });
  }

  formatDuration(minutes: number): string {
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  }

  recomputeAllSpots(): void {
    if (this.isRecomputing) return;
    this.isRecomputing = true;
    this.recomputeStatus = '';
    this.http.post<any>(`${environment.apiUrl}/fishing_spots/recompute`, {}).subscribe({
      next: (res) => {
        if (res.job_id) this.jobService.register({ id: res.job_id, job_type: 'spots_recompute', entity_type: 'global', entity_id: null, status: 'queued', message: 'Recomputing all spots...', created_at: new Date().toISOString(), completed_at: null });
        this.isRecomputing = false;
        this.recomputeStatus = '';
      },
      error: () => {
        this.isRecomputing = false;
        this.recomputeStatus = 'Failed to queue recompute.';
      }
    });
  }

  toggleAddForm(): void {
    this.showAddForm = !this.showAddForm;
    if (!this.showAddForm) {
      this.newLat = '';
      this.newLng = '';
      this.newName = '';
      this.newLocation = '';
    }
  }

  saveNewSpot(): void {
    const lat = parseFloat(this.newLat);
    const lng = parseFloat(this.newLng);
    if (isNaN(lat) || isNaN(lng)) return;
    this.isSavingNew = true;
    this.http.post<SpotRow>(`${environment.apiUrl}/fishing_spots`, {
      center_lat: lat,
      center_lng: lng,
      name: this.newName || null,
      location: this.newLocation || null,
    }).subscribe({
      next: (spot) => {
        this.isSavingNew = false;
        this.showAddForm = false;
        this.newLat = '';
        this.newLng = '';
        this.newName = '';
        this.newLocation = '';
        this.spots.data = [spot, ...this.spots.data];
      },
      error: () => { this.isSavingNew = false; }
    });
  }
}
